import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Star, Clock, Users, Award, Heart, Leaf, Sparkles } from 'lucide-react';
import { BOOKING_URL } from "@/constants/urls";
import skinHealthImageWebP from "@/assets/treatment.webp";
import skinHealthImageJPG from "@/assets/treatment.jpg";

const SkinPackagePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 px-4 md:px-6 bg-sage-50 border-b border-amber-100">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200 mb-4">
                ***Intoductory Offer*** Available for bookings until end of September
              </Badge>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent leading-tight mb-4">
                Glow Begins Beneath the Surface
              </h1>
              <p className="text-lg md:text-xl text-sage-700 leading-relaxed mb-2">
                Radiant skin starts from within. Discover Cosmetic Acupuncture and natural skincare this September.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4 mb-8">
                <Button
                  asChild
                  size="lg"
                  className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg font-medium"
                >
                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book Your Glow Session
                  </a>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="border-amber-600 text-amber-600 hover:bg-amber-50 px-8 py-4 text-lg font-medium"
                >
                  <a href="#treatments">View Complete Treatment Information</a>
                </Button>
              </div>
            </div>
            <div className="relative flex justify-center lg:justify-end">
              <div className="w-full max-w-md">
                <picture>
                  <source srcSet={skinHealthImageWebP} type="image/webp" />
                  <img
                    src={skinHealthImageJPG}
                    alt="Cosmetic acupuncture treatment for radiant, balanced skin using Founders Formula skincare"
                    className="rounded-2xl shadow-2xl w-full h-auto"
                    loading="eager"
                    decoding="async"
                    aria-describedby="skin-package-image-description"
                  />
                </picture>
              </div>
              <div id="skin-package-image-description" className="sr-only">
                                  Professional cosmetic acupuncture treatment session showing the gentle application of cosmetic acupuncture needles combined with premium Founders Formula skincare for natural skin transformation.
              </div>
              <div className="absolute -bottom-4 -right-4 bg-white rounded-lg shadow-lg p-4 border border-amber-200">
                <div className="flex items-center space-x-2">
                  <Star className="h-5 w-5 text-amber-500 fill-current" />
                  <span className="text-sm font-medium text-sage-700">Introductory Offer Available Now</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 px-4 md:px-6 bg-white border-b border-amber-100">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
              Discover Radiant, Balanced Skin – From the Inside Out
            </h2>
            <p className="text-lg text-sage-700 leading-relaxed mb-6">
              At <em>Women and the Way</em>, our skin treatments go beyond surface beauty. We combine ancient Chinese Medicine with modern facial techniques to treat the <strong>root cause</strong> of skin imbalances and bring your glow back - naturally.
            </p>
            <p className="text-lg text-sage-700 leading-relaxed">
              Whether you're experiencing hormonal acne, dry or sensitive skin, signs of aging, or just feeling dull and depleted, our treatments are designed to <strong>nourish from within</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* Treatments Section */}
      <section id="treatments" className="py-16 px-4 md:px-6 bg-sage-50 border-b border-amber-100">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
              Choose Your Skin Journey
            </h2>
            <p className="text-lg text-sage-700 leading-relaxed max-w-3xl mx-auto">
              Whether you're new to cosmetic acupuncture or ready for deeper transformation, we have the perfect treatment to meet your skin goals.
            </p>
          </div>

          {/* Introductory Treatment - Full Width */}
          <div className="mb-16">
            <div className="text-center mb-8">
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200 mb-4">
                ***Intoductory Offer*** Available for bookings until end of September
              </Badge>
              <h3 className="font-serif text-2xl md:text-3xl font-bold text-sage-800 mb-2">
                Start Your Journey: Introductory Cosmetic Express
              </h3>
              <p className="text-lg text-sage-600">
                Perfect for first-time clients or those wanting a gentle introduction to cosmetic acupuncture
              </p>
            </div>

            <Card className="border-2 border-amber-200 shadow-xl bg-white max-w-4xl mx-auto">
              <CardHeader className="text-center pb-6">
                <CardTitle className="text-2xl font-serif text-sage-800">
                  Introductory Cosmetic Express Treatment
                </CardTitle>
                <CardDescription className="text-lg text-sage-600">
                  Your 60-Minute Ritual to Restore Radiance
                </CardDescription>
                <div className="flex items-center justify-center space-x-4 mt-4">
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-amber-500" />
                    <span className="text-sm text-sage-600">60 minutes</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Users className="h-4 w-4 text-amber-500" />
                    <span className="text-sm text-sage-600">Perfect for beginners</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-semibold text-sage-800 text-lg mb-3">What's Included:</h4>
                  <ul className="space-y-2 text-sage-700">
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span>Gentle <strong>Cleanse</strong> with Founders Formula</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Lymphatic Gua Sha</strong> using <em>Founders Formula</em> Oil</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Cosmetic Acupuncture</strong> to brighten and soften lines</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Australian Clay Mask</strong> + personalised botanical mask</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Body Acupuncture</strong> to reset internal balance</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Pump & Glow Mask</strong> for hydration and radiance</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span><strong>LED Light Therapy</strong> (bonus this month – normally $40)</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-sage-50 rounded-lg p-6">
                  <h4 className="font-semibold text-sage-800 mb-3">Introductory Offer Pricing:</h4>
                  <ul className="space-y-2 text-sage-700">
                    <li className="flex items-center space-x-2">
                      <Heart className="h-4 w-4 text-amber-500" />
                      <span><strong>Existing Patients:</strong> $185 /session</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <Heart className="h-4 w-4 text-amber-500" />
                      <span><strong>New Patients:</strong> Initial $230 | Return $190 /session</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <Heart className="h-4 w-4 text-amber-500" />
                      <span><strong>Intro Pack of 10 Treatments:</strong> $1665 (Save $815)</span>
                    </li>
                  </ul>
                  <p className="text-sm text-amber-600 font-medium mt-3">Limited spaces available –Book Now - offer expires September</p>
                </div>

                <div className="text-center pt-4">
                  <Button
                    asChild
                    size="lg"
                    className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg font-medium"
                  >
                    <a
                      href={BOOKING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Book Introductory Treatment
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Complete Treatment - Full Width */}
          <div>
            <div className="text-center mb-8">
              <Badge className="bg-sage-100 text-sage-800 hover:bg-sage-200 mb-4">
                Complete Transformation
              </Badge>
              <h3 className="font-serif text-2xl md:text-3xl font-bold text-sage-800 mb-2">
                Deep Healing: Constitutional Cosmetic Acupuncture
              </h3>
              <p className="text-lg text-sage-600">
                For those ready to address the root cause of skin concerns with comprehensive treatment
              </p>
            </div>

            <Card className="border-2 border-sage-200 shadow-xl bg-white max-w-4xl mx-auto">
              <CardHeader className="text-center pb-6">
                <CardTitle className="text-2xl font-serif text-sage-800">
                  Constitutional Cosmetic Acupuncture
                </CardTitle>
                <CardDescription className="text-lg text-sage-600">
                  Complete Skin Transformation from Within
                </CardDescription>
                <div className="flex items-center justify-center space-x-4 mt-4">
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-sage-500" />
                    <span className="text-sm text-sage-600">90-120 minutes</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Award className="h-4 w-4 text-sage-500" />
                    <span className="text-sm text-sage-600">Comprehensive treatment</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <p className="text-sage-700 mb-4 text-lg">
                    Designed to treat the internal root of skin conditions like hormonal breakouts, chronic dryness, inflammation, and early aging. This comprehensive approach addresses both the symptoms and underlying causes.
                  </p>
                  <h4 className="font-semibold text-sage-800 text-lg mb-3">Includes everything in the Introductory Treatment <em>plus</em>:</h4>
                  <ul className="space-y-2 text-sage-700">
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-4 w-4 text-sage-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Hot Stone Massage</strong> for neck and shoulders</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-4 w-4 text-sage-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Body Gua Sha + Cupping</strong> for lymph flow and detoxification</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-4 w-4 text-sage-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Extended Acupuncture</strong> to support digestion, hormones, stress</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-4 w-4 text-sage-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Founders Formula Skincare</strong> protocol + LED therapy</span>
                    </li>

                  </ul>
                </div>

                <div className="bg-sage-50 rounded-lg p-6">
                  <h4 className="font-semibold text-sage-800 mb-3">Investment:</h4>
                  <ul className="space-y-2 text-sage-700">
                    <li className="flex items-center space-x-2">
                      <Heart className="h-4 w-4 text-sage-500" />
                      <span><strong>Initial Consultation:</strong> $320 /session</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <Heart className="h-4 w-4 text-sage-500" />
                      <span><strong>Return Sessions:</strong> $255 /session</span>
                    </li>
                  </ul>
                  <p className="text-sm text-sage-600 mt-3">Recommended: 6-8 sessions for optimal results</p>
                </div>

                <div className="text-center pt-4">
                  <Button
                    asChild
                    size="lg"
                    className="bg-sage-600 hover:bg-sage-700 text-white px-8 py-4 text-lg font-medium"
                  >
                    <a
                      href={BOOKING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Book Complete Treatment
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Optional Blood Analysis Section */}
      <section className="py-12 px-4 md:px-6 bg-white border-b border-amber-100">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-8">
            <Badge className="bg-sage-100 text-sage-800 hover:bg-sage-200 mb-4">
              Optional Enhancement
            </Badge>
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-sage-800 mb-2">
              Optional Integrated Blood Analysis
            </h3>
            <p className="text-lg text-sage-600">
              Get deeper insights into your skin health with comprehensive blood testing
            </p>
          </div>

          <Card className="border-2 border-sage-200 shadow-lg bg-white max-w-2xl mx-auto">
            <CardContent className="p-6">
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <Award className="h-5 w-5 text-sage-500" />
                  <span className="text-lg font-semibold text-sage-800">$50</span>
                </div>
                
                <div className="text-sage-700 space-y-3">
                  <p className="text-lg">
                    Add this comprehensive blood analysis to any treatment for deeper insights into your skin health.
                  </p>
                  
                  <div className="bg-sage-50 rounded-lg p-4">
                    <h4 className="font-semibold text-sage-800 mb-2">Tests Included:</h4>
                    <ul className="text-sage-700 space-y-1">
                      <li>• Iron levels</li>
                      <li>• Vitamin B12</li>
                      <li>• Zinc levels</li>
                      <li>• Liver function</li>
                      <li>• And other key markers</li>
                    </ul>
                  </div>
                  
                  <p className="text-sm text-sage-600">
                    Results help us tailor your treatment plan for optimal skin health outcomes.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Founders Formula Section */}
      <section className="py-16 px-4 md:px-6 bg-white border-b border-amber-100">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
              We Use the Best in Botanical Skincare
            </h2>
            <p className="text-lg text-sage-700 leading-relaxed">
              Every treatment is powered by <strong>Founders Formula</strong>  -  an award-winning, Australian-made skincare brand. Formulated with potent botanicals, these products are designed to calm, strengthen, and visibly transform the skin barrier.
            </p>
          </div>
        </div>
      </section>

      {/* Skin Concerns Section */}
      <section className="py-16 px-4 md:px-6 bg-sage-50 border-b border-amber-100">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
              Skin Concerns We Treat
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="bg-white border-amber-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <Sparkles className="h-5 w-5 text-amber-500" />
                  <h3 className="font-semibold text-sage-800">Hormonal Acne</h3>
                </div>
                <p className="text-sage-600 text-sm">Address the root cause of breakouts with constitutional treatment</p>
              </CardContent>
            </Card>

            <Card className="bg-white border-amber-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <Leaf className="h-5 w-5 text-amber-500" />
                  <h3 className="font-semibold text-sage-800">Dry & Dull Skin</h3>
                </div>
                <p className="text-sage-600 text-sm">Restore hydration and natural radiance from within</p>
              </CardContent>
            </Card>

            <Card className="bg-white border-amber-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <Award className="h-5 w-5 text-amber-500" />
                  <h3 className="font-semibold text-sage-800">Redness & Reactivity</h3>
                </div>
                <p className="text-sage-600 text-sm">Calm inflammation and strengthen skin barrier</p>
              </CardContent>
            </Card>

            <Card className="bg-white border-amber-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <Star className="h-5 w-5 text-amber-500" />
                  <h3 className="font-semibold text-sage-800">Fine Lines & Aging</h3>
                </div>
                <p className="text-sage-600 text-sm">Support collagen production and skin elasticity</p>
              </CardContent>
            </Card>

            <Card className="bg-white border-amber-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <Users className="h-5 w-5 text-amber-500" />
                  <h3 className="font-semibold text-sage-800">Facial Tension</h3>
                </div>
                <p className="text-sage-600 text-sm">Release jaw clenching and muscle tension</p>
              </CardContent>
            </Card>

            <Card className="bg-white border-amber-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <Heart className="h-5 w-5 text-amber-500" />
                  <h3 className="font-semibold text-sage-800">Post-Pill & Postpartum</h3>
                </div>
                <p className="text-sage-600 text-sm">Support skin through hormonal transitions</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 md:px-6 bg-sage-50">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
            Book Your Glow
          </h2>
          <p className="text-xl text-sage-600 mb-8 max-w-2xl mx-auto">
            Limited introductory spaces available. Start your skin healing journey with care, calm, and confidence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg font-medium"
            >
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Book Now
              </a>
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="border-amber-600 text-amber-600 hover:bg-amber-50 px-8 py-4 text-lg font-medium"
            >
                                  <a href="tel:0415537784">Call Us at 0415 537 784</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SkinPackagePage; 